import { GEO_REDIRECT_TRACKING_COOKIE } from '~/utils/geo';
import { resolveGeoPreferenceRedirect } from '~/utils/geoRedirect';

/**
 * Preference-based region auto-routing. A returning
 * visitor whose stored region preference mismatches the served cluster is
 * redirected to the preferred region, path and (sanitised) query preserved —
 * but only for paths mirrored in every cluster. Unmirrored paths never redirect
 * here; `UnifiedRegionGeoRouting` instead offers the destination homepage via a
 * confirmation dialog. The redirect decision lives in `resolveGeoPreferenceRedirect`.
 *
 * SSR-only: the served cluster is fixed for a client session (cross-cluster
 * navigation is always a full page load), so a mismatch can only arise on an
 * initial request — running on client navigations would redirect nothing yet
 * leak a cookie watcher per navigation. 302, not 301: the target depends on a
 * per-visitor cookie, not a permanent URL move.
 */
export default defineNuxtRouteMiddleware((to) => {
  if (import.meta.client) {
    return;
  }

  // The dominant case platform-wide (every visitor without a stored
  // preference, including all crawlers) exits here before any policy input
  // is assembled — this middleware runs on every SSR request.
  if (!useRegionPreference().value) {
    return;
  }

  const config = useRuntimeConfig();
  const { input, currentCluster } = useGeoPreferenceTargetInput();
  const redirectMarker = useCookie<string | null>(GEO_REDIRECT_TRACKING_COOKIE, { sameSite: 'lax', secure: true });

  const target = resolveGeoPreferenceRedirect({
    ...input.value,
    path: to.path,
    query: to.query,
    scheme: config.public.useHttp ? 'http' : 'https',
  });
  if (!target) {
    return;
  }

  // No analytics provider exists server-side, so mark the redirect (with the
  // origin region) for the landing page to report — see
  // `useGeoRouting.trackCompletedRedirect`. Rides the 302's Set-Cookie.
  redirectMarker.value = currentCluster.value?.geo ?? null;
  return navigateTo(target, { external: true, replace: true, redirectCode: 302 });
});
